import { useState, useEffect, useCallback } from 'react';

export interface CookieConsent {
  essential: boolean;   // always true
  analytics: boolean;
  advertising: boolean;
}

const STORAGE_KEY = 'tg_cookie_consent';

function readConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookieConsent;
  } catch {
    return null;
  }
}

function writeConsent(consent: CookieConsent): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // localStorage full or blocked — silently continue
  }
}

export function useCookieConsent() {
  const [consent, setConsent] = useState<CookieConsent | null>(() => readConsent());
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Show banner after short delay if no consent stored
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [consent]);

  const acceptAll = useCallback(() => {
    const c: CookieConsent = { essential: true, analytics: true, advertising: true };
    writeConsent(c);
    setConsent(c);
    setShowBanner(false);
  }, []);

  const rejectOptional = useCallback(() => {
    const c: CookieConsent = { essential: true, analytics: false, advertising: false };
    writeConsent(c);
    setConsent(c);
    setShowBanner(false);
  }, []);

  const savePreferences = useCallback((prefs: Omit<CookieConsent, 'essential'>) => {
    const c: CookieConsent = { essential: true, ...prefs };
    writeConsent(c);
    setConsent(c);
    setShowBanner(false);
  }, []);

  const resetConsent = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setConsent(null);
    setShowBanner(true);
  }, []);

  return {
    consent,
    showBanner,
    hasConsented: consent !== null,
    allowsAnalytics: consent?.analytics ?? false,
    allowsAdvertising: consent?.advertising ?? false,
    acceptAll,
    rejectOptional,
    savePreferences,
    resetConsent,
  };
}
